import axios, { type AxiosProgressEvent } from 'axios';

import { type Common } from '../common.js';
import { type DocumentWithTemplate } from '../models/previsit/document.js';
import {
  type CreateValues,
  type DocumentTemplate,
  type DocumentTemplateWithProviders,
  type TextCommand,
  type UpdateValues,
} from '../models/previsit/documentTemplate.js';
import { type relationshipMap } from '../models/previsit.js';

export class API {
  common: Common;
  baseEndpoint = '/previsit/document-templates';

  constructor(common: Common) {
    this.common = common;
  }

  async findAllByFormSetAndField(
    formSetId: string,
    formFieldId: string
  ): Promise<DocumentWithTemplate[]> {
    const url = this.common.constructPath(
      `/patient-previsit/form-sets/${formSetId}/fields/${formFieldId}/documents`
    );
    return (await this.common.getJSON(url)).documents;
  }

  getDownloadUrl(document: DocumentWithTemplate): string {
    return this.common.constructPath(`/patient-previsit/documents/${document.id}/download`);
  }

  getPreviewUrl(document: DocumentWithTemplate): string {
    return this.common.constructPath(`/patient-previsit/documents/${document.id}/preview`);
  }

  async generateSignedDocuments(args: {
    formSetId: string;
    formFieldId: string;
    documentToSignIds?: string[];
    documentToConfirmIds?: string[];
    documentNotSignedIds?: string[];
    signedValue?: {
      disclosureAgreed: boolean;
      signingFor: string | null;
      signatureText: string;
      // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
      guardianRelationship: string | keyof typeof relationshipMap | null;
    };
  }): Promise<{ success: boolean }> {
    const {
      formSetId,
      formFieldId,
      documentToSignIds,
      documentToConfirmIds,
      documentNotSignedIds,
      signedValue,
    } = args;
    const url = this.common.constructPath(
      `/patient-previsit/documents/${formSetId}/${formFieldId}/sign`
    );
    return await this.common.postJSON(url, {
      documentToSignIds,
      documentNotSignedIds,
      documentToConfirmIds,
      signedValue,
    });
  }

  async getPreview(template: { gsPath: string; commands: TextCommand[] }): Promise<Blob> {
    const url = this.common.constructPath(`${this.baseEndpoint}/preview`);
    const response = await this.common.authedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(template),
    });

    if (response.status === 200) {
      return await response.blob();
    }

    if (response.headers.get('Content-Type') === 'application/json') {
      const json = await response.json();
      console.error(`Response had status ${response.status}`, json);
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      throw new Error(json.message || 'Unsuccessful request');
    }
    const text = await response.text();
    console.error(`Response had status ${response.status}`, text);
    throw new Error('Unsuccessful request');
  }

  async findAll(query: { documentTemplateId?: string } = {}): Promise<DocumentTemplate[]> {
    const url = this.common.constructPath(this.baseEndpoint, query);
    const res = await this.common.getJSON(url);
    return res.documentTemplates;
  }

  async findOneById(id: string): Promise<DocumentTemplateWithProviders> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return (await this.common.getJSON(url)).documentTemplate;
  }

  async update(id: string, values: UpdateValues): Promise<DocumentTemplateWithProviders> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return (
      await this.common.putJSON(url, {
        name: values.name,
        gsPath: values.gsPath,
        category: values.category,
        commands: values.commands,
        previsitFormTemplateId: values.previsitFormTemplateId,
      })
    ).documentTemplate;
  }

  async remove(id: string): Promise<{ numDeleted: number }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }

  async create(values: CreateValues): Promise<DocumentTemplateWithProviders> {
    const url = this.common.constructPath(`${this.baseEndpoint}/`);
    const added = await this.common.postJSON(url, values);
    return added.documentTemplate;
  }

  async findMaybeOneByFormTemplateId(
    formTemplateId: string
  ): Promise<DocumentTemplate | undefined> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/linked-form-template/${formTemplateId}`
    );
    const response = await this.common.getJSON(url);
    return response?.document;
  }

  downloadURL(id: string): string {
    return this.common.constructPath(`${this.baseEndpoint}/${id}/file`);
  }

  async getSignedUploadURL(fileName: string): Promise<string> {
    const url = this.common.constructPath(`${this.baseEndpoint}/upload-url`, { fileName });
    const response = await this.common.postJSON(url, {});
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return response?.url || '';
  }

  async upload(
    file: File,
    signedUploadURL: string,
    onUploadProgress: (event: AxiosProgressEvent) => void
  ): Promise<number> {
    const response = await axios.put(signedUploadURL, file, {
      onUploadProgress,
      headers: { 'Content-Type': 'application/pdf' },
    });
    return response.status;
  }
}
